import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { me } from '@/src/api/user'
import { Tabs, TabsContent } from '@/src/components/ui/tabs'
import { LinkIcon } from '@phosphor-icons/react'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { StatusSwitch } from '@/src/components/ui/status-switch'
import { Spinner } from '@/src/components/ui/spinner'
import { useTranslation } from '@/src/i18n/client'
import { userCanManageOrganizationSettings } from '@/src/lib/permissionHelpers'
import { UpsertFormLayout, UpsertFormSection } from '@/src/components/manage/UpsertFormLayout'
import { OrganizationWorkflowSecurityTab } from '@/src/components/manage/organization/settings/OrganizationWorkflowSecurityTab'
import { OrganizationIntegrationsTab } from '@/src/components/manage/organization/settings/OrganizationIntegrationsTab'
import { OrganizationGemsDataHubTab } from '@/src/components/manage/organization/settings/OrganizationGemsDataHubTab'
import { ToolbarTabButton } from '@/src/components/layout/ToolbarTabToggle'

export const Route = createFileRoute('/_manage/manage/organization/settings')({
  component: OrgSettingsPage,
  validateSearch: (search: Record<string, unknown>) => {
    const tab = search.tab as string | undefined
    if (tab === 'workflow-security' || tab === 'docuware-sync' || tab === 'gems-data-hub') {
      return { tab }
    }

    return { tab: 'workflow-security' }
  },
  beforeLoad: async ({ context }: any) => {
    // Permissions checked inside the component
  },
})

function OrgSettingsPage() {
  const { t } = useTranslation('manage')
  const navigate = Route.useNavigate()
  const { tab } = Route.useSearch()
  const [docuwareEnabled, setDocuwareEnabled] = useState(false)
  const [gemsEnabled, setGemsEnabled] = useState(false)

  const { data: user, isLoading } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: () => me(),
  })

  const permissions: string[] = (user?.role?.permissions as string[]) ?? []
  const isAdmin = permissions.includes('*')
  const canManageOrg = userCanManageOrganizationSettings(permissions, isAdmin)

  const orgId = user?.organization?.id

  const handleTabChange = (value: string) => {
    navigate({ search: { tab: value } })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner />
      </div>
    )
  }

  if (!canManageOrg || !orgId) {
    return (
      <div>
        <AdminToolbar>
          <AdminToolbar.Left>
            <AdminToolbar.Title className="text-xl font-semibold">{t('organizations.settings')}</AdminToolbar.Title>
          </AdminToolbar.Left>
        </AdminToolbar>
        <div className="container py-8 text-muted-foreground">
          {!orgId
            ? t('organizations.noOrganization')
            : t('organizations.noPermission')}
        </div>
      </div>
    )
  }

  return (
    <div>
      <AdminToolbar>
        <AdminToolbar.Left>
          <div className="flex gap-1">
              <ToolbarTabButton active={tab === 'workflow-security'} onClick={() => handleTabChange('workflow-security')}>
                <LinkIcon className="h-4 w-4" />
                {t('organizations.settingsTabs.workflowSecurity')}
              </ToolbarTabButton>
              <ToolbarTabButton active={tab === 'docuware-sync'} onClick={() => handleTabChange('docuware-sync')}>
                <img src="/assets/docuware.svg" alt="DocuWare" className="h-4 w-4" />
                {t('organizations.settingsTabs.docuwareSync')}
              </ToolbarTabButton>
              <ToolbarTabButton active={tab === 'gems-data-hub'} onClick={() => handleTabChange('gems-data-hub')}>
                <img src="/assets/gems.svg" alt="GEMS" className="h-4 w-4" />
                {t('organizations.settingsTabs.gemsDataHubSync')}
              </ToolbarTabButton>
          </div>
        </AdminToolbar.Left>
        {tab === 'docuware-sync' && (
          <AdminToolbar.Right>
            <StatusSwitch
              id="docuware-enabled"
              label={t('organizations.docuwareEnabled')}
              checked={docuwareEnabled}
              onCheckedChange={setDocuwareEnabled}
            />
          </AdminToolbar.Right>
        )}
        {tab === 'gems-data-hub' && (
          <AdminToolbar.Right>
            <StatusSwitch
              id="gems-enabled"
              label={t('organizations.gemsEnabled')}
              checked={gemsEnabled}
              onCheckedChange={setGemsEnabled}
            />
          </AdminToolbar.Right>
        )}
      </AdminToolbar>

      <div className="container py-6">
        <UpsertFormLayout
          onCancel={() => navigate({ to: '/manage/organization', search: { tab: 'overview' } })}
          onSubmit={(e) => e.preventDefault()}
          showActions={false}
          maxWidth="full"
        >
          <UpsertFormSection>
            <Tabs value={tab} onValueChange={handleTabChange}>
              <TabsContent value="workflow-security">
                <OrganizationWorkflowSecurityTab orgId={orgId} isSystemAdmin={isAdmin} />
              </TabsContent>
              <TabsContent value="docuware-sync">
                <OrganizationIntegrationsTab orgId={orgId} enabled={docuwareEnabled} onEnabledChange={setDocuwareEnabled} />
              </TabsContent>
              <TabsContent value="gems-data-hub">
                <OrganizationGemsDataHubTab />
              </TabsContent>
            </Tabs>
          </UpsertFormSection>
        </UpsertFormLayout>
      </div>
    </div>
  )
}
